/**
 * useWorkspaceMilestones — Project Harmony · Patch 21 · Patch 34.2.1d
 *
 * Deterministic milestone signals that power progressive disclosure.
 * Milestones are computed from data we already fetch: autopilot
 * dashboard + campaign list + editorial state + vault rollup.
 *
 * Patch 34.2.1d · Every internal query is now workspace-scoped —
 * cache keys include the workspace id and `enabled` gates on it —
 * so switching workspaces cannot serve a prior workspace's cached
 * milestones. The old zero-argument signature has been removed;
 * every call site must pass the current workspace id.
 */
import { useQuery } from "@tanstack/react-query";
import { http } from "@/shared/api/http";
import { getAutopilotDashboard } from "@/features/autopilot/api";

export interface WorkspaceMilestones {
  /** At least one campaign blueprint has been created. */
  hasFirstBlueprint: boolean;
  /** At least one campaign has completed asset approval. */
  hasAssetsApproved: boolean;
  /** At least one social platform is connected (publishing-ready). */
  hasSocialConnected: boolean;
  /** Autopilot has been enabled at some point. */
  hasAutopilotEnabled: boolean;
  /** Vault contains 100+ assets → advanced Vault tools unlock. */
  hasVaultDepth: boolean;
  /** Total vault-registered asset count (approved+scheduled+published). */
  assetCount: number;
  /** Approved-only count (in vault, not yet scheduled/published). */
  approvedCount: number;
  /** Scheduled-only count. */
  scheduledCount: number;
  /** Published-only count. */
  publishedCount: number;
  /** Draft assets awaiting review (Patch 34.1.13). */
  draftCount: number;
  /** Assets flagged ``needs_review`` — a stricter review queue. */
  needsReviewCount: number;
  /** Total campaign count. */
  campaignCount: number;
  /** Loading flag for the compound query. */
  isLoading: boolean;
  /**
   * Patch 34.2.1e · TRUE when the blueprints list request failed.
   * Consumers must NOT interpret a blueprints-list failure as
   * "no blueprints exist" — that is the exact bug we removed with the
   * try/catch cleanup. When `blueprintsError` is true, `hasFirstBlueprint`
   * defaults to FALSE but consumers should treat it as "unknown".
   */
  blueprintsError: boolean;
  /** Patch 34.2.1e · TRUE when the vault-rollup request failed. */
  rollupError: boolean;
}

interface BlueprintListResponse {
  blueprints: Array<{ id: string; status: string }>;
}

async function listBlueprints(): Promise<BlueprintListResponse> {
  // Patch 34.2.1e · Errors propagate — no silent try/catch fallback.
  // Callers must inspect `blueprintsError` to distinguish "no
  // blueprints" from "blueprint API unreachable".
  return http<BlueprintListResponse>("/orchestration/blueprints");
}

interface VaultRollup {
  total: number;
  approved: number;
  scheduled: number;
  published: number;
  needs_review?: number;
  draft?: number;
  archived?: number;
  backfilled_on_this_call?: number;
}

async function assetsRollup(): Promise<VaultRollup> {
  // Patch 34.2.1d · Rollup is workspace-scoped via the X-Workspace-Id
  // header set on the shared http() client. Errors are surfaced (no
  // defensive empty fallback) so the caller can distinguish "genuinely
  // empty" from "backend unreachable".
  return http<VaultRollup>("/vault/rollup");
}

export function useWorkspaceMilestones(
  workspaceId: string | null | undefined,
): WorkspaceMilestones {
  const ready = Boolean(workspaceId);

  const autopilot = useQuery({
    queryKey: ["autopilot-dashboard", workspaceId ?? "no-workspace"],
    queryFn: getAutopilotDashboard,
    enabled: ready,
    refetchInterval: 60_000,
    retry: false,
  });
  const blueprints = useQuery({
    queryKey: ["blueprints", workspaceId ?? "no-workspace", "harmony"],
    queryFn: listBlueprints,
    enabled: ready,
    refetchInterval: 60_000,
    retry: false,
  });
  const assets = useQuery({
    queryKey: ["assets-rollup", workspaceId ?? "no-workspace", "harmony"],
    queryFn: assetsRollup,
    enabled: ready,
    refetchInterval: 60_000,
    retry: false,
  });

  const bpList = blueprints.data?.blueprints ?? [];
  const hasFirstBlueprint = bpList.length > 0;
  const hasAssetsApproved = bpList.some(
    (b) => ["executing", "approved", "completed"].includes(b.status),
  );
  const ap = autopilot.data;
  const hasAutopilotEnabled = Boolean(ap?.enabled_at) || (ap?.tick_count ?? 0) > 0;
  const hasSocialConnected = ap ? ap.pending_reviews < ap.campaigns_running : false;
  const rollup = assets.data;
  const assetCount = rollup?.total ?? 0;
  const approvedCount = rollup?.approved ?? 0;
  const scheduledCount = rollup?.scheduled ?? 0;
  const publishedCount = rollup?.published ?? 0;
  const draftCount = rollup?.draft ?? 0;
  const needsReviewCount = rollup?.needs_review ?? 0;
  const hasVaultDepth = assetCount >= 100;

  return {
    hasFirstBlueprint,
    hasAssetsApproved,
    hasSocialConnected,
    hasAutopilotEnabled,
    hasVaultDepth,
    assetCount,
    approvedCount,
    scheduledCount,
    publishedCount,
    draftCount,
    needsReviewCount,
    campaignCount: bpList.length,
    isLoading:
      ready && (autopilot.isLoading || blueprints.isLoading || assets.isLoading),
    // Patch 34.2.1e · surface upstream errors so the UI never
    // masquerades "API failed" as "no blueprints" or "empty vault".
    blueprintsError: blueprints.isError,
    rollupError: assets.isError,
  };
}
